import React, { useState, useEffect } from 'react';

function UserModal({ user, onSave, onClose }) {
  const [formData, setFormData] = useState({
    user_id: '',
    jira_username: '',
    jira_id: '',
    timezone: 'PT',
    country: 'US',
    queues: ['support'],
    capped_user: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        user_id: user.user_id,
        jira_username: user.jira_username,
        jira_id: user.jira_id,
        timezone: user.timezone,
        country: user.country,
        queues: user.queues || ['support'],
        capped_user: user.capped_user,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQueueChange = (queue) => {
    setFormData(prev => {
      const queues = prev.queues.includes(queue)
        ? prev.queues.filter(q => q !== queue)
        : [...prev.queues, queue];
      return { ...prev, queues };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Validation
      if (!formData.user_id || !formData.jira_username || !formData.jira_id) {
        throw new Error('Please fill in all required fields');
      }
      if (formData.queues.length === 0) {
        throw new Error('Please select at least one queue');
      }

      await onSave(formData);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{user ? 'Edit User' : 'Create New User'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>User ID *</label>
            <input
              type="text"
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              placeholder="john.doe"
              disabled={!!user}
              required
            />
            <small>Username portion of email (read-only after creation)</small>
          </div>

          <div className="form-group">
            <label>Jira Username (Email) *</label>
            <input
              type="email"
              name="jira_username"
              value={formData.jira_username}
              onChange={handleChange}
              placeholder="john.doe@instacart.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Jira Account ID *</label>
            <input
              type="text"
              name="jira_id"
              value={formData.jira_id}
              onChange={handleChange}
              placeholder="5f3dcc3efcaf93003bc13bc7"
              required
            />
            <small>Find in Jira user profile</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Timezone *</label>
              <select name="timezone" value={formData.timezone} onChange={handleChange}>
                <option value="ET">Eastern (ET)</option>
                <option value="CT">Central (CT)</option>
                <option value="MT">Mountain (MT)</option>
                <option value="PT">Pacific (PT)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Country *</label>
              <select name="country" value={formData.country} onChange={handleChange}>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="AUS">Australia</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Queues *</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.queues.includes('support')}
                  onChange={() => handleQueueChange('support')}
                />
                Support Queue
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.queues.includes('crm')}
                  onChange={() => handleQueueChange('crm')}
                />
                CRM Queue
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="capped_user"
                checked={formData.capped_user}
                onChange={handleChange}
              />
              New Hire Cap (limit to 5 concurrent tickets)
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (user ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserModal;
